import { useState, useEffect, useRef } from 'react'
import {
  ModalProps,
  Modal,
  Space,
  Descriptions,
  Button,
  Tabs,
  Icons,
  Form,
  Row,
  Select,
  Typography,
  DateField,
  Tag,
  useForm,
  message,
  Input,
} from '@pankod/refine-antd'
import { useTable, useCreate, useUpdate } from '@pankod/refine-core'
import { CSVLink } from 'react-csv'
import { IPaper, IPaperUsageAnalysis, ITestEventAssignedPapers } from 'interfaces'
import { Data } from 'react-csv/components/CommonPropTypes'

import testEventAssignedPapersMetaData from 'metadata/testEventAssignedPapers'
import { paperExportHelper } from 'helpers/paper/questionAnswerKey'
import { examOnlineQuestionUploadApiIntegration } from 'helpers/exam-online/api-integration'

type exportModalProps = {
  modalProps: ModalProps
  request: IPaper
  closeModal: any
  showModal: any
  exportData: string | Data | (() => string | Data)
  exportDataLoading: boolean
  fetchExportData?: (isHtml?: boolean) => Promise<string | undefined>
}

const { ExportOutlined } = Icons
const { Text } = Typography

export const ExportModal: React.FC<exportModalProps> = ({
  modalProps,
  request,
  closeModal,
  showModal,
  exportDataLoading,
  exportData,
  fetchExportData,
}) => {
  const [eventDetailsForExport, setEventDetailsForExport] = useState<ITestEventAssignedPapers>()
  const [currentActiveKey, setCurrentActiveKey] = useState<string>('same_event')
  const [selectedTestEvent, setSelectedTestEvent] = useState<string>('')
  const [exportPaperFormat, setExportPaperFormat] = useState<string>('default')
  const [questionBankName, setQuestionBankName] = useState<string>('')
  const [customPaperDownloadLoading, setCustomPaperDownloadLoading] = useState<boolean>(false)
  const [htmlPaperExportData, setHtmlPaperExportData] = useState<{
    loading: boolean
    data?: any
  }>({
    loading: false,
    data: '',
  })
  const csvLinkRef = useRef<any>(null)

  const { mutate: mutatePaperUsageAnalysis } = useCreate<IPaperUsageAnalysis>()
  const { mutate: mutatePaperStatus } = useUpdate<IPaper>()

  const { tableQueryResult: listOfEventsQueryResult } = useTable<ITestEventAssignedPapers>({
    resource: 'test_event_assigned_papers',
    permanentFilter: [{ field: 'paper_id', operator: 'eq', value: request?.id }],
    metaData: testEventAssignedPapersMetaData,
    queryOptions: { enabled: !!request && !!modalProps?.visible },
  })

  const handleDisableExport = () => {
    if (request?.active === true) {
      if (currentActiveKey === 'different_event') {
        console.log('selected eventt here ', selectedTestEvent)
        return selectedTestEvent.length === 0
      } else return false
    } else return true
  }

  const handleExport = () => {
    if (request?.id !== undefined) {
      mutatePaperStatus({
        id: request?.id,
        resource: 'papers',
        values: { export_count: request.export_count + 1 },
      })
    }

    // add function to mutate paper usage analysis
    if (currentActiveKey === 'same_event') {
      const slotData = JSON.parse(request?.slot_data!)
      const date = slotData?.date
      const start_time = slotData?.start_time
      const end_time = slotData?.end_time
      mutatePaperUsageAnalysis({
        resource: 'paper_usage_analysis',
        values: {
          paper_id: request?.id,
          product_id: request?.product_id,
          test_event_id: request?.test_event_id,
          date,
          start_time,
          end_time,
        },
      })
    } else if (currentActiveKey === 'different_event') {
      const { date, start_time, end_time } = eventDetailsForExport?.slot_assigned!
      mutatePaperUsageAnalysis({
        resource: 'paper_usage_analysis',
        values: {
          paper_id: request?.id,
          product_id: request?.product_id,
          test_event_id: eventDetailsForExport?.test_event_id,
          date,
          start_time,
          end_time,
        },
      })
    }
  }

  const onKeyChange = (key: string) => {
    console.log('current tab ', key)
    setSelectedTestEvent('')
    setCurrentActiveKey(key)
  }

  const exportPaperWithHtml = async () => {
    if (fetchExportData) {
      setHtmlPaperExportData(prev => ({ ...prev, loading: true }))
      const htmlIncludedPaperData = await fetchExportData(true)
      setHtmlPaperExportData({ loading: false, data: htmlIncludedPaperData || '' })
    }
  }

  const RenderSlot = ({ data, disabled }: { data: any; disabled: boolean }) => {
    const parsedData = JSON.parse(data)
    return (
      <>
        <DateField value={parsedData?.date} format="LL" disabled={disabled} />
        <Tag>
          <Space>
            <DateField value={parsedData?.start_time} format="LT" disabled={disabled} />
            <Text disabled={disabled}>To</Text>
            <DateField value={parsedData?.end_time} format="LT" disabled={disabled} />
          </Space>
        </Tag>
      </>
    )
  }
  const paperExportFormats = [
    { value: 'default', label: 'Default' },
    { value: 'autoproctor', label: 'Autoproctor' },
    { value: 'examonline', label: 'Exam-Online' },
  ]
  return (
    <>
      <Button onClick={() => showModal()}>Export Options</Button>
      <Modal
        destroyOnClose
        {...modalProps}
        // style={{ minWidth: '1000px', minHeight: '30%' }}
        style={{ width: '90%', maxWidth: 1000 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
        title="Export Paper"
        footer={
          <>
            <Space>
              <Button
                key="close"
                onClick={() => {
                  closeModal()
                }}
              >
                Close
              </Button>
              {exportPaperFormat === 'default' ? (
                <Button key="export paper" type="default" disabled={handleDisableExport()} onClick={() => handleExport()}>
                  {exportDataLoading ? (
                    'Loading...'
                  ) : (
                    <CSVLink
                      key="export"
                      data={exportData}
                      filename={
                        request?.name
                          ? `Paper_${request?.name}_${Math.round(new Date().getTime() / 1000)}_[${request?.paper_id}].csv`
                          : `Paper_${request?.paper_id}-${request?.paper_smart_id}_${Math.round(new Date().getTime() / 1000)}.csv`
                      }
                    >
                      <ExportOutlined /> Export Paper
                    </CSVLink>
                  )}
                </Button>
              ) : exportPaperFormat === 'autoproctor' ? (
                <Button
                  key="custom-export"
                  loading={customPaperDownloadLoading}
                  onClick={async () => {
                    setCustomPaperDownloadLoading(true)
                    const downloadCustom = await paperExportHelper(exportPaperFormat, request)
                    if (!downloadCustom) {
                      message.error('Unable to download at the moment. Please try again later.')
                    }
                    setCustomPaperDownloadLoading(false)
                  }}
                >
                  Export Paper
                </Button>
              ) : (
                <Button
                  key="upload-paper"
                  loading={customPaperDownloadLoading}
                  onClick={async () => {
                    setCustomPaperDownloadLoading(true)
                    if (questionBankName) {
                      const uploadQuestion: any = await paperExportHelper(exportPaperFormat, request, questionBankName)
                      if (uploadQuestion.status) {
                        message.success(uploadQuestion?.message)
                      } else {
                        message.error(uploadQuestion?.message)
                      }
                    } else {
                      message.warning('Please provide already created Question Bank Name!')
                    }
                    setCustomPaperDownloadLoading(false)
                  }}
                >
                  Upload Paper
                </Button>
              )}

              {exportPaperFormat === 'default' ? (
                <Button
                  key="export-with-tags"
                  type="default"
                  disabled={handleDisableExport()}
                  onClick={async () => {
                    await exportPaperWithHtml()
                    csvLinkRef.current?.link.click()
                    handleExport()
                  }}
                >
                  {htmlPaperExportData.loading ? (
                    <>
                      <ExportOutlined /> Loading...
                    </>
                  ) : (
                    <>
                      <ExportOutlined /> Export Paper With Tags
                    </>
                  )}
                </Button>
              ) : null}
              <CSVLink
                data={htmlPaperExportData.data || []}
                filename={
                  request?.name
                    ? `Paper_${request?.name}_${Math.round(new Date().getTime() / 1000)}_[${request?.paper_id}]_(WITH TAGS).csv`
                    : `Paper_With_Tags_${request?.paper_id}-${request?.paper_smart_id}_${Math.round(new Date().getTime() / 1000)}_(WITH TAGS).csv`
                }
                className="hidden"
                ref={csvLinkRef}
              />
            </Space>
          </>
        }
      >
        <Form>
          <Tabs
            type="line"
            animated={false}
            defaultActiveKey="same_event"
            activeKey={currentActiveKey}
            onChange={onKeyChange}
            // onChange={ changeTab}
          >
            <Tabs.TabPane tab="Same Event" key="same_event">
              <Descriptions title="Export Details" labelStyle={{ fontWeight: 'bold' }} column={1}>
                <Descriptions.Item label="Test Event">{request?.test_event?.name}</Descriptions.Item>
                <Descriptions.Item label="Slot">
                  <RenderSlot data={request?.slot_data} disabled={false} />
                </Descriptions.Item>
                <Descriptions.Item label="Format">
                  <Select value={exportPaperFormat} onChange={value => setExportPaperFormat(value)}>
                    {paperExportFormats.map(format => (
                      <option value={format.value}>{format.label}</option>
                    ))}
                  </Select>
                </Descriptions.Item>
                {exportPaperFormat === 'examonline' && (
                  <Descriptions.Item label="Question Bank Name">
                    <Input
                      key={'question_bank_name'}
                      placeholder="QB name"
                      onChange={e => {
                        setQuestionBankName(e.target.value)
                      }}
                    />
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Different Event" key="different_event">
              <Row>
                <Form.Item label="Select an Event" required={currentActiveKey === 'different_event' ? true : false} name="test_event">
                  <Select
                    size="middle"
                    style={{ minWidth: 200 }}
                    onChange={value => {
                      const event = JSON.parse(value)
                      setSelectedTestEvent(event?.test_event?.id)
                      setEventDetailsForExport(JSON.parse(value))
                    }}
                  >
                    {listOfEventsQueryResult?.data?.data?.map((data: ITestEventAssignedPapers) => (
                      <option key={data?.id} value={JSON.stringify(data)}>
                        {data?.test_event?.name}
                      </option>
                    ))}
                  </Select>
                </Form.Item>
              </Row>
            </Tabs.TabPane>
          </Tabs>
        </Form>
      </Modal>
    </>
  )
}
